import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { EKeyInitiativeGoalMode } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { IFormKIEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { selectPartnerGeoMarket } from 'legacy/lut/features/partner360/configuration'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { FC, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { useActualsTypes } from 'partner360/components/actualsTypeButtons'
import { QualityDonut, Tag } from 'partner360/components/qualityIndicator'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { INITIATIVES_EDITOR_TYPE_LEGACY } from 'partner360/features/keyInitiativeEditor/model/editorConfig'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { TKeyInitiativesFinancialData } from 'partner360/features/keyInitiatives/model/financialData'
import { filterBusinessPriorities } from 'partner360/features/keyInitiatives/ui/helpers/filterBusinessPriorities'
import { useHandleShowAddNewDialog } from 'partner360/features/keyInitiatives/ui/hooks/useHandleShowAddNewDialog'
import { useNewKeyInitiative } from 'partner360/features/keyInitiatives/ui/hooks/useNewKeyInitiative'
import { getInitiativesPlanQuality } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/helpers/getInitiativesPlanQuality'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { KeyInitiativesQualityContent } from './KeyInitiativesQualityContent'

import styles from './KeyInitiativesQuality.module.scss'

interface IInitiativesPlanQualityProps {
  data: TKeyInitiative[]
  finData: TKeyInitiativesFinancialData[]
  businessPriorities: IKIBusinessPriority[]
  activeCurrency: string
  refetchKI: () => void
  isGlobal: boolean
  isRollupMode: boolean
  fYear: number
  programType: ProgramTypes
}

export const KeyInitiativesQuality: FC<IInitiativesPlanQualityProps> = ({
  data,
  finData,
  businessPriorities,
  activeCurrency,
  refetchKI,
  isGlobal,
  fYear,
  programType,
  isRollupMode,
}) => {
  const { actualsTypes, actualsTypeDefault } = useActualsTypes()
  const actualsType = actualsTypes.includes(ActualsValueTypes.TNet)
    ? ActualsValueTypes.TNet
    : actualsTypeDefault
  const { keyObjectives } = useSelector(selectJBPSettings)
  const partnerMarket = useSelector(selectPartnerGeoMarket)
  const isEditMode = useSelector(selectPublishingMode)
  const { isPartnerUser } = useUserRoles()
  const [isLoading, setIsLoading] = useState(false)
  const [newInitiativeGoalMode, setNewInitiativeGoalMode] = useState<EKeyInitiativeGoalMode>()

  const { showAddNewDialog, handleShowNewDialog, handleCloseNewDialog } =
    useHandleShowAddNewDialog()

  const { sourceItem, resetSourceItem, setSourceItem } = useNewKeyInitiative<
    Partial<IFormKIEditor>
  >({ handleShowNewDialog })

  const filteredBusinessPriorities = useMemo(
    () =>
      filterBusinessPriorities({
        priorities: businessPriorities,
        fYear,
        programType,
        market: partnerMarket,
      }),
    [businessPriorities, fYear, partnerMarket, programType]
  )

  const quality = useMemo(
    () =>
      getInitiativesPlanQuality({
        data,
        keyObjectives,
        finData,
        businessPriorities: filteredBusinessPriorities,
        activeCurrency,
        actualsType,
      }),
    [activeCurrency, actualsType, data, filteredBusinessPriorities, finData, keyObjectives]
  )

  const closeKIDialog = useCallback(() => {
    handleCloseNewDialog()
    resetSourceItem()
    setNewInitiativeGoalMode(undefined)
  }, [handleCloseNewDialog, resetSourceItem])

  const onCreateIncrementalInitiative = useCallback(() => {
    resetSourceItem()
    setNewInitiativeGoalMode(EKeyInitiativeGoalMode.Incremental)
    handleShowNewDialog()
  }, [handleShowNewDialog, resetSourceItem])

  const onEditInitiative = useCallback(
    (initiative: TKeyInitiative) => {
      const editableInitiative: Partial<TKeyInitiative> = initiative

      setSourceItem(editableInitiative as IFormKIEditor)
      setNewInitiativeGoalMode(undefined)
      handleShowNewDialog()
    },
    [handleShowNewDialog, setSourceItem]
  )

  const canEdit = !isRollupMode && isEditMode && !isPartnerUser

  return (
    <P360Card className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />

      <div className={styles.content}>
        <div className={styles.chart} data-tour-id={TOUR_TARGETS.qualityScore}>
          <div className={styles.title}>
            <Translation
              text={TRANSLATION_KEYS.quality.title}
              replaceParams={[
                {
                  key: '{metric}',
                  value: actualsType === ActualsValueTypes.Ndp ? 'NDP' : actualsType,
                },
                { key: '{currency}', value: activeCurrency },
              ]}
            />
          </div>

          <div className={styles.subtitle}>
            <Translation text={TRANSLATION_KEYS.quality.subtitle} />
          </div>

          <div className={styles.donut}>
            <QualityDonut score={quality.overall.score} band={quality.overall.band} />

            <Tag
              variant={quality.overall.band}
              translation={
                <Translation
                  text={TRANSLATION_KEYS.quality.overall.title}
                  replaceParams={[{ key: '{band}', value: quality.overall.band }]}
                />
              }
            />
          </div>
        </div>

        <KeyInitiativesQualityContent
          quality={quality}
          activeCurrency={activeCurrency}
          canEdit={canEdit}
          onEditInitiative={onEditInitiative}
          onCreateIncrementalInitiative={onCreateIncrementalInitiative}
          tourId={TOUR_TARGETS.qualityPillars}
        />
      </div>

      <KeyInitiativeEditor
        initialState={sourceItem}
        newInitiativeGoalMode={newInitiativeGoalMode}
        isDialogOpened={showAddNewDialog}
        isDataInsightPresent={programType !== ProgramTypes.Retail}
        onLoadingStateChange={setIsLoading}
        onEditCancel={closeKIDialog}
        onUpdateFinished={() => {
          closeKIDialog()
          refetchKI()
        }}
        keyInitiativesFormatOverride={isGlobal ? INITIATIVES_EDITOR_TYPE_LEGACY : undefined}
      />
    </P360Card>
  )
}
